import { getCache, setCache } from '@/utils/cache';
import { get } from '@/utils/request';
import { defineStore } from 'pinia';
import ct from 'countries-and-timezones';
import _ from 'lodash';
import type { ValueLabel } from '@/types';

function mapLevels(data: any) {
  data.influencerLevels = {};
  const influencerLevels = data.channels_active[1].levels;
  influencerLevels.forEach((elem: any) => {
    const name = elem.label.split(' ')[0];
    let elemMap = { ...elem };
    if (elem.value !== 'META') {
      elemMap = { ...elem, label: elem.label.split(' ')[1] };
    }
    if (!data.influencerLevels[name]) {
      data.influencerLevels[name] = [];
      data.influencerLevels[name].push(elemMap);
    } else {
      data.influencerLevels[name].push(elemMap);
    }
  });
}

type ConfigKey = 'country' | 'active_countries' | 'locations' | 'genders' | 'channels' | 'post_content_types';

export const useConfsStore = defineStore('confs', {
  state: () => ({
    conf: getCache('confs') || null,
    categories: [] as any[],
    paymentMethods: null as any[] | null,
    contentCreatorTypes: [] as any[],
    loading: false,
    currentCurrency: '',
    error: null as string | any | null,
    privacy: null as any,
    terms: null as any,
    faq: null as any,
  }),

  actions: {
    async doGetAllConfs() {
      this.loading = true;
      const data = await get('/conf/all');
      data.subcategories = [];
      data.categories.forEach((cat: any) => {
        data.subcategories = [...data.subcategories, cat.subcategories];
      });
      mapLevels(data);
      data.categories = _.orderBy(data.categories, 'label');
      this.conf = data;

      // for el-ui parent : children : []
      this.categories = data.categories.map((e: any) => ({
        value: e.enum_value,
        label: e.en_label,
        children: e.subcategories.sort((a: any, b: any) => a.label.localeCompare(b.label)),
      }));
      setCache('confs', this.conf);
      //await this.doGetActiveCountries();
      this.loading = false;
    },

    async doGetChannelsForCountry(country: string) {
      return await get(`/conf/channels/countries/${country}`);
    },

    async doGetBudgetMinMaxByCurrency(currency: string) {
      const data = await get(`/conf/budget_min_max/${currency}`);
      return data;
    },

    //* Get social media png src from VALUE
    getChannelSrcFromValue(value: string) {
      return this.conf.channels.find((e: any) => e.value === value)?.icon_location ?? '';
    },
    getChannelFromValue(value: string) {
      return this.conf.channels.find((e: any) => e.value === value);
    },

    getItem(configKey: ConfigKey, value: string | number) {
      return this.conf[configKey].find((e: any) => e.value === value);
    },
    getCities(countryCode: string): ValueLabel[] {
      if (countryCode) {
        return this.conf.all_locations
          .filter((e: any) => e.country == countryCode)
          .sort((a: any, b: any) => a.label.localeCompare(b.label));
      }
      return [];
    },
    getCountry(valueStr: string) {
      return this.conf.countries.find((e: any) => e.value == valueStr);
    },
    getCountryCode(valueStr: string) {
      return this.conf.countries.find((e: any) => e.label == valueStr);
    },
    getCountryTimezone(countryCode: string) {
      return ct.getTimezonesForCountry(countryCode);
    },
    getLocation(valueStr: string) {
      return this.conf.all_locations.find((e: any) => e.value === valueStr);
    },
    getPostContentType(valueStr: string | string[]) {
      let contentTypes: any[] = [];
      this.conf.channels.forEach((channel: any) => {
        contentTypes = [...contentTypes, ...channel.post_content_types];
      });

      // Handle backwards compatibility - if it's a string, return single object
      if (typeof valueStr === 'string') {
        return contentTypes.find(e => e.value === valueStr);
      }

      // If it's an array, return array of matching objects
      if (Array.isArray(valueStr)) {
        return valueStr.map(value => contentTypes.find(e => e.value === value)).filter(Boolean);
      }

      // Fallback for undefined/null
      return null;
    },
    async doGetContentCreatorTypes() {
      this.contentCreatorTypes = await get(`/content-creator-types`);
    },

    getChannelContentType(channel: string) {
      if (this.conf && channel !== '') {
        return this.conf.channels?.find((c: any) => c.value === channel).post_content_types;
      }
      return [];
    },
    getVerificationStatusLabel(status: string) {
      if (status === 'NOT_VERIFIED') return 'Pending';
      return this.conf.verification_statuses?.find((e: any) => e.value === status)?.label;
    },
    getPreLiveCheckStatusLabel(status: string) {
      return this.conf.pre_live_check_statuses?.find((e: any) => e.value === status)?.label;
    },

    // ---- Footer confs ---------------------------------------------------------------------

    async doGetPrivacy() {
      this.error = null;
      try {
        const data = await get('/conf/privacy/MERCHANT/latest');
        this.privacy = data.label;
      } catch (e) {
        this.error = e;
      }
    },
    async doGetTerms() {
      this.error = null;
      try {
        this.terms = (await get('/conf/terms/MERCHANT/latest')).label;
      } catch (e) {
        this.error = e;
      }
    },

    async doGetFAQ(faqType: string) {
      this.error = null;
      try {
        const data = await get(`/faqs/by_type/${faqType}`);
        this.faq = data;
      } catch (e) {
        this.error = e;
      }
    },

    async doGetCurrentCurrencyByCountry(country: string): Promise<any> {
      this.currentCurrency = await get(`/conf/currencies/countries/${country}`);
      return this.currentCurrency;
    },
  },
  getters: {
    occupations(): any[] {
      return this.conf.categories
        .find((e: any) => e.enum_value === 'PROFESSION')
        .subcategories.sort((a: any, b: any) => a.label.localeCompare(b.label));
    },
    influencerCategories(): any[] {
      return this.conf.categories
        .find((e: any) => e.enum_value !== 'PROFESSION')
        .subcategories.sort((a: any, b: any) => a.label.localeCompare(b.label));
    },
    influencerSubcategories(): any[] {
      const arr: any[] = [];
      this.conf.categories.forEach((category: any) => {
        arr.push(...category.subcategories);
      });
      arr.sort((a: any, b: any) => a.label.localeCompare(b.label));
      return arr;
    },
    influencerLevels(): any[] {
      if (this.conf.channels) {
        return this.conf.channels[0].levels.filter((e: any) => e.value !== 'NONE' && e.value !== 'INVALID');
      }
      return [];
    },
    activeCountries(): any[] {
      return this.conf.active_countries.sort((a: any, b: any) => a.label.localeCompare(b.label));
    },
    allPostContentTypes(): any[] {
      const contentTypes: any[] = [];
      this.conf.channels.forEach((channel: any) => {
        contentTypes.push(...channel.post_content_types);
      });
      return contentTypes;
    },
    bonusTypes() {
      return [
        { value: 'NONE', label: 'Standard rate' },
        { value: 'EXTRA', label: 'Bonus payment' },
        { value: 'FIXED', label: 'Custom rate' },
      ];
    },
  },
});
